import { useState, useEffect, useCallback } from "react";
import { Video, Plus, Trash2, ChevronRight, ChevronDown, ArrowUp, ArrowDown, Youtube, Loader2 } from "lucide-react";
import axios from "axios";
import { toast } from "sonner";
import { YouTubeEmbed } from "./YouTubeEmbed";

const VimeoEmbed = ({ videoId, title = "Vimeo video" }) => (
  <div className="relative w-full rounded-xl overflow-hidden bg-black" style={{ aspectRatio: "16/9" }} data-testid="vimeo-embed">
    <iframe
      src={`https://player.vimeo.com/video/${videoId}?title=0&byline=0&portrait=0`}
      title={title}
      loading="lazy"
      allow="autoplay; fullscreen; picture-in-picture; clipboard-write"
      allowFullScreen
      className="absolute inset-0 w-full h-full"
      style={{ border: 0 }}
    />
  </div>
);

const AddVideoModal = ({ organIndex, onClose, onCreated, apiUrl, lang }) => {
  const [title, setTitle] = useState("");
  const [url, setUrl] = useState("");
  const [description, setDescription] = useState("");
  const [busy, setBusy] = useState(false);
  const isEn = lang === "en";

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!title.trim() || !url.trim()) {
      toast.error(isEn ? "Title and URL required" : "Tajuk dan URL diperlukan");
      return;
    }
    setBusy(true);
    try {
      const res = await axios.post(`${apiUrl}/admin/organs/${organIndex}/videos`, {
        title: title.trim(),
        youtube_url: url.trim(),
        description: description.trim(),
      });
      toast.success(isEn ? "Video added" : "Video ditambah");
      onCreated(res.data);
      onClose();
    } catch (err) {
      toast.error(err.response?.data?.detail || (isEn ? "Failed to add video" : "Gagal tambah video"));
    } finally {
      setBusy(false);
    }
  };

  return (
    <div className="fixed inset-0 z-50 bg-black/50 backdrop-blur-sm flex items-center justify-center p-4" onClick={onClose} data-testid="add-video-modal">
      <form
        onSubmit={handleSubmit}
        onClick={(e) => e.stopPropagation()}
        className="bg-white rounded-2xl shadow-2xl w-full max-w-lg p-6 space-y-4"
      >
        <div>
          <h3 className="text-lg font-bold text-[#0A0A0A] flex items-center gap-2">
            <Youtube size={18} className="text-red-500" />
            {isEn ? `Add Video — Organ ${organIndex}` : `Tambah Video — Organ ${organIndex}`}
          </h3>
          <p className="text-xs text-gray-400 mt-1">
            {isEn ? "Paste a YouTube or Vimeo link" : "Tampal URL YouTube atau Vimeo"}
          </p>
        </div>

        <div>
          <label className="text-xs font-semibold text-gray-600 mb-1 block">{isEn ? "Title" : "Tajuk"}</label>
          <input
            type="text"
            value={title}
            onChange={(e) => setTitle(e.target.value)}
            placeholder={isEn ? "e.g. Vision & Mission Walkthrough" : "cth. Panduan Vision & Mission"}
            className="w-full px-3 py-2 rounded-xl border border-gray-200 text-sm focus:outline-none focus:ring-2 focus:ring-[#2563EB]"
            data-testid="video-title-input"
            required
          />
        </div>

        <div>
          <label className="text-xs font-semibold text-gray-600 mb-1 block">{isEn ? "Video URL" : "URL Video"}</label>
          <input
            type="url"
            value={url}
            onChange={(e) => setUrl(e.target.value)}
            placeholder="https://www.youtube.com/watch?v=..."
            className="w-full px-3 py-2 rounded-xl border border-gray-200 text-sm focus:outline-none focus:ring-2 focus:ring-[#2563EB]"
            data-testid="video-url-input"
            required
          />
        </div>

        <div>
          <label className="text-xs font-semibold text-gray-600 mb-1 block">{isEn ? "Description (optional)" : "Keterangan (opsyenal)"}</label>
          <textarea
            value={description}
            onChange={(e) => setDescription(e.target.value)}
            rows={2}
            placeholder={isEn ? "Brief context for students" : "Konteks ringkas untuk pelajar"}
            className="w-full px-3 py-2 rounded-xl border border-gray-200 text-sm focus:outline-none focus:ring-2 focus:ring-[#2563EB] resize-none"
            data-testid="video-desc-input"
          />
        </div>

        <div className="flex items-center justify-end gap-2 pt-2">
          <button
            type="button"
            onClick={onClose}
            className="px-4 py-2 rounded-full text-sm text-gray-500 hover:bg-gray-100 transition-colors"
          >
            {isEn ? "Cancel" : "Batal"}
          </button>
          <button
            type="submit"
            disabled={busy}
            className="inline-flex items-center gap-1.5 px-4 py-2 rounded-full bg-[#2563EB] text-white text-sm font-semibold hover:bg-[#1d4ed8] disabled:opacity-60 transition-colors"
            data-testid="save-video-btn"
          >
            {busy ? <Loader2 size={14} className="animate-spin" /> : <Plus size={14} />}
            {isEn ? "Add" : "Tambah"}
          </button>
        </div>
      </form>
    </div>
  );
};

/**
 * Shows YouTube/Vimeo videos for a specific Organ (1-5).
 * Admin can add/delete/reorder; students watch inline.
 */
export const OrganVideos = ({ apiUrl, organIndex, lang = "ms", isAdmin = false }) => {
  const [videos, setVideos] = useState([]);
  const [loading, setLoading] = useState(true);
  const [adding, setAdding] = useState(false);
  const [busyId, setBusyId] = useState(null);
  const [expanded, setExpanded] = useState(() => {
    try {
      const stored = localStorage.getItem(`bss.videos.expanded.${organIndex}`);
      return stored !== "false"; // default open
    } catch (_e) {
      return true;
    }
  });

  const isEn = lang === "en";

  const toggleExpanded = () => {
    const next = !expanded;
    setExpanded(next);
    try { localStorage.setItem(`bss.videos.expanded.${organIndex}`, String(next)); } catch (_e) { /* noop */ }
  };

  const fetchVideos = useCallback(async () => {
    setLoading(true);
    try {
      const res = await axios.get(`${apiUrl}/organs/${organIndex}/videos`);
      setVideos(res.data.videos || []);
    } catch {
      setVideos([]);
    } finally {
      setLoading(false);
    }
  }, [apiUrl, organIndex]);

  useEffect(() => { fetchVideos(); }, [fetchVideos]);

  const handleDelete = async (video) => {
    if (!window.confirm(isEn ? `Delete "${video.title}"?` : `Padam "${video.title}"?`)) return;
    setBusyId(video.id);
    try {
      await axios.delete(`${apiUrl}/admin/organs/${organIndex}/videos/${video.id}`);
      toast.success(isEn ? "Video deleted" : "Video dipadam");
      await fetchVideos();
    } catch (err) {
      toast.error(err.response?.data?.detail || (isEn ? "Failed to delete" : "Gagal padam"));
    } finally {
      setBusyId(null);
    }
  };

  const handleMove = async (video, direction) => {
    setBusyId(video.id);
    try {
      await axios.post(`${apiUrl}/admin/organs/${organIndex}/videos/${video.id}/move?direction=${direction}`);
      await fetchVideos();
    } catch (err) {
      toast.error(err.response?.data?.detail || (isEn ? "Failed to move" : "Gagal gerak"));
    } finally {
      setBusyId(null);
    }
  };

  if (!isAdmin && !loading && videos.length === 0) return null;

  return (
    <>
      <div className="dash-card" data-testid={`organ-${organIndex}-videos`}>
        <button
          type="button"
          onClick={toggleExpanded}
          className="w-full dash-card-header text-left hover:bg-[#FAFAFA] transition-colors"
          data-testid={`organ-${organIndex}-videos-toggle`}
          aria-expanded={expanded}
        >
          <h3 className="flex items-center gap-2">
            {expanded ? <ChevronDown size={14} className="text-gray-400" /> : <ChevronRight size={14} className="text-gray-400" />}
            <Video size={16} className="text-red-500" />
            {isEn ? `Videos — Organ ${organIndex}` : `Video — Organ ${organIndex}`}
          </h3>
          <div className="flex items-center gap-2" onClick={(e) => e.stopPropagation()}>
            <span className="text-xs text-gray-400">
              {videos.length} {isEn ? "videos" : "video"}
            </span>
            {isAdmin && (
              <button
                type="button"
                onClick={(e) => { e.stopPropagation(); setAdding(true); }}
                className="flex items-center gap-1.5 px-3 py-1.5 rounded-full text-[11px] font-semibold text-white bg-[#2563EB] hover:bg-[#1d4ed8] transition-colors"
                data-testid={`organ-${organIndex}-add-video-btn`}
              >
                <Plus size={12} /> {isEn ? "Add" : "Tambah"}
              </button>
            )}
          </div>
        </button>

        {expanded && (
          <div className="p-3">
            {loading ? (
              <div className="py-4 text-center text-xs text-gray-400">
                <Loader2 size={14} className="inline animate-spin mr-2" />
                {isEn ? "Loading…" : "Memuatkan…"}
              </div>
            ) : videos.length === 0 ? (
              <div className="text-center py-6 text-xs text-[rgba(0,0,0,0.4)] italic">
                {isAdmin
                  ? (isEn ? `No videos yet. Click "Add" to share a YouTube/Vimeo link.` : `Belum ada video. Klik "Tambah" untuk kongsi link YouTube/Vimeo.`)
                  : (isEn ? "No videos available yet." : "Belum ada video.")}
              </div>
            ) : (
              <div className="space-y-5">
                {videos.map((v, i) => (
                  <div key={v.id} className="group" data-testid="organ-video-item">
                    {v.provider === "vimeo" ? (
                      <VimeoEmbed videoId={v.video_id} title={v.title} />
                    ) : (
                      <YouTubeEmbed videoId={v.video_id} title={v.title} />
                    )}
                    <div className="mt-2 flex items-start justify-between gap-3">
                      <div className="min-w-0 flex-1">
                        <p className="text-sm font-semibold text-[#0A0A0A] truncate" title={v.title}>
                          {v.title}
                        </p>
                        {v.description && (
                          <p className="text-xs text-gray-500 mt-0.5 leading-relaxed line-clamp-2">
                            {v.description}
                          </p>
                        )}
                      </div>
                      {isAdmin && (
                        <div className="flex items-center gap-1 flex-shrink-0">
                          <button
                            onClick={() => handleMove(v, "up")}
                            disabled={i === 0 || busyId === v.id}
                            className="w-7 h-7 rounded-full hover:bg-[#F7F7F7] flex items-center justify-center transition-colors disabled:opacity-30"
                            title={isEn ? "Move up" : "Alih atas"}
                            data-testid="video-move-up"
                          >
                            <ArrowUp size={13} className="text-[#0A0A0A]" />
                          </button>
                          <button
                            onClick={() => handleMove(v, "down")}
                            disabled={i === videos.length - 1 || busyId === v.id}
                            className="w-7 h-7 rounded-full hover:bg-[#F7F7F7] flex items-center justify-center transition-colors disabled:opacity-30"
                            title={isEn ? "Move down" : "Alih bawah"}
                            data-testid="video-move-down"
                          >
                            <ArrowDown size={13} className="text-[#0A0A0A]" />
                          </button>
                          <button
                            onClick={() => handleDelete(v)}
                            disabled={busyId === v.id}
                            className="w-7 h-7 rounded-full hover:bg-red-50 flex items-center justify-center transition-colors"
                            title={isEn ? "Delete" : "Padam"}
                            data-testid="video-delete"
                          >
                            {busyId === v.id ? <Loader2 size={13} className="animate-spin text-red-500" /> : <Trash2 size={13} className="text-red-500" />}
                          </button>
                        </div>
                      )}
                    </div>
                  </div>
                ))}
              </div>
            )}
          </div>
        )}
      </div>

      {adding && (
        <AddVideoModal
          organIndex={organIndex}
          apiUrl={apiUrl}
          lang={lang}
          onClose={() => setAdding(false)}
          onCreated={() => fetchVideos()}
        />
      )}
    </>
  );
};
